<template>
  <div>
    <v-row>
      <v-col md="5" class="pb-0 hidden-sm-and-down">
        <v-img
          height="100vh"
          :src="registerDogImage"
          class="sticky-image"
        />
      </v-col>
      <register-litter-form-1
        v-if="this.currentPage === 1"
        :damInfo="damInfo"
        :changePage="changePage"
        :searchDam="searchDam"
        :dogId="dogId"
        :picker="picker"
        :changeDate="changeDate"
        :changeWhelpingDateExplanation="changeWhelpingDateExplanation"
        :changeArtificialInsemination="changeArtificialInsemination"
        :whelpingDateExplanation="whelpingDateExplanation"
        :isArtificialInsemination="isArtificialInsemination"
        :validateWhelpingDate="validateWhelpingDate"
        :validationInfo="validationInfo"
        :minDate="minDate"
        :maxDate="maxDate"
        :priceInfo="priceInfo"
        :feesLoaded="feesLoaded"
      />
      <register-litter-form-2
        v-if="this.currentPage === 2"
        :changePage="changePage"
        :changeSireInfo="changeSireInfo"
        :damInfo="damInfo"
        :sireInfo="sireInfo"
        :changePuppyInfo="changePuppyInfo"
        :puppies="puppies"
        :colors="colors"
      />
      <register-litter-form-3
        v-if="this.currentPage === 3"
        :changePage="changePage"
        :status="registrationRequestStatus"
        :damInfo="damInfo"
        :sireInfo="sireInfo"
        :puppies="puppies"
        :updateSignature="updateSignature"
        :signature="signature"
        :sendRegistrationRequest="sendRegistrationRequest"
        :fee="validationInfo.fee"
        :selectedKennels="requiredKennelIds"
        :priceInfo="priceInfo"
        :isArtificialInsemination="isArtificialInsemination"
        :feesLoaded="feesLoaded"
      />
    </v-row>
  </div>
</template>

<script lang="ts">
import Vue from "vue";

import AppNavbar from "@/components/Layouts/AppNavbar.vue";
import RegisterLitterForm1 from "./Steps/RegisterLitterForm1.vue";
import RegisterLitterForm2 from "./Steps/RegisterLitterForm2.vue";
import RegisterLitterForm3 from "./Steps/RegisterLitterForm3.vue";

import {
  searchDam,
  submitLitterRegistrationRequest,
  validateWhelpingDate,
  RegisterLitterRequest,
  WhelpingDateValidation,
} from "@/services/portal/registerLitterService";
import lookupsService from "@/services/lookupsService";
import workflowService from "@/services/portal/workflowService";
import { getUserToday } from "@/helpers/date-utils";

import { UserInfo } from "@/types/User";
import AuthorizeNetResponseData from "@/types/AuthorizeNetResponseData";

const registerDogImage = require('@/assets/register-dog.png');

export default Vue.extend({
  name: "RegisterLitter",
  components: {
    AppNavbar,
    RegisterLitterForm1,
    RegisterLitterForm2,
    RegisterLitterForm3,
  },
  data: function () {
    return {
      registerDogImage,
      currentPage: 1,
      picker: null as Temporal.PlainDate | null,
      minDate: null as Temporal.PlainDate | null,
      maxDate: null as Temporal.PlainDate | null,
      damInfo: {} as any,
      sireInfo: [] as any[],
      puppies: {} as any[],
      registrationRequestStatus: {},
      colors: {},
      priceInfo: [],
      feesLoaded: false,
      requiredKennelIds: [] as string[],
      signature: "",
      isArtificialInsemination: false,
      whelpingDateExplanation: "",
      validationInfo: {} as WhelpingDateValidation,
      requestResponse: {},
    };
  },
  methods: {
    changePage(page: number) {
      this.currentPage = page;
    },
    changeDate(date: Temporal.PlainDate | null) {
      this.picker = date;
    },
    searchDam(dogId: string, date: Temporal.PlainDate) {
      searchDam(dogId, date).then((res) => {
        this.damInfo = res.data;
        this.requiredKennelIds = [];
        this.damInfo.ownersKennels.forEach((k: any) => {               
          this.requiredKennelIds.push(k.kennelId);
        });
      }).catch((error) => {
        this.damInfo = {};
        this.requiredKennelIds = [];
        this.$handleError(error);
      });
    },
    validateWhelpingDate(damId: string, date: Temporal.PlainDate) {
      validateWhelpingDate(damId, date).then((res) => {
        this.validationInfo = res;
        this.whelpingDateExplanation = "";
      }).catch((error) => {
        this.validationInfo = {} as WhelpingDateValidation;
        this.$handleError(error);
      });
    },
    changeSireInfo(sireInfo: any[]) {
      this.sireInfo = sireInfo;
      sireInfo.forEach((s) => {
        if (!this.puppies[s.dogId]) {
          this.$set(this.puppies, s.dogId, []);
        }
      });
    },
    changePuppyInfo(puppies: object[], sireId: string, deletePuppies: boolean) {
      if (deletePuppies) {
        Vue.delete(this.puppies, sireId);
      } else {
        this.$set(this.puppies, sireId, puppies);
      }
    },
    updateSignature(signature: string) {
      this.signature = signature;
    },
    changeArtificialInsemination(val: boolean) {
      this.isArtificialInsemination = val;
    },
    changeWhelpingDateExplanation(value: string) {
      this.whelpingDateExplanation = value;
    },
    sendRegistrationRequest(paymentInfo: AuthorizeNetResponseData, signatureText: string) {
      // dam
      const damOwners = [] as { PersonId: number; IsPrimary: boolean }[];
      if (!!this.damInfo.lease) {
        this.damInfo.lease.lesees.forEach((o: any) =>
          damOwners.push({PersonId: o.personId, IsPrimary: o.isPrimary})
        );
      } else {
        this.damInfo.primaryOwners.forEach((o: any) =>
          damOwners.push({PersonId: o.personId, IsPrimary: o.isPrimary})
        );
      }

      const dam = {
        dogId: this.damInfo.dogId,
        owners: damOwners,
        leaseInfo: this.damInfo.lease,
      };

      // puppies
      const puppies = [];
      for (const sire in this.puppies) {
        for (
          let puppyIdx = 0;
          puppyIdx < this.puppies[sire].length;
          puppyIdx++
        ) {
          puppies.push({ ...this.puppies[sire][puppyIdx], sireId: sire });
        }
      }
      const request: RegisterLitterRequest = {
        paymentInfo,
        requesterSignatureText: signatureText,
        whelpingDate: this.picker!,
        requiredKennelIds: this.requiredKennelIds,
        isArtificialInsemination: this.isArtificialInsemination,
        whelpingDateExplanation: this.whelpingDateExplanation,
        fee: this.validationInfo.fee! * this.sireInfo.length,
        dam,
        sires: this.sireInfo,
        isMultiSire: this.sireInfo.length > 1,
        puppies,
      };

      submitLitterRegistrationRequest(request).then((res) => {
        this.registrationRequestStatus = res;
      }).catch((error) => {
        this.registrationRequestStatus = error;
      });
    },
  },
  computed: {
    userInfo(): UserInfo {
      return this.$store.state.userInfo;
    },
  },
  created() {
    if (
      this.$route.query.start_date &&
      typeof this.$route.query.start_date === "string"
    ) {
      const startDate = Temporal.PlainDate.from(this.$route.query.start_date);
      this.minDate = startDate;
      this.picker = startDate;
    }

    this.maxDate = getUserToday();

    lookupsService.getPuppyAttributes().then((res) => {
      this.colors = res;
    });

    workflowService.getRegisterLitterFees().then((res) => {
      this.priceInfo = res;
      this.feesLoaded = true;
    }).catch(this.$handleError);
  },
  watch: {
    "damInfo.ownersKennels": {
      handler: function (newVal: any, oldVal: any) {
        if (newVal === oldVal) return;
        if (newVal) {
          this.$data.availableKennels = newVal;
        }
      },
    },
  },
  props: {
    dogId: String,
  },
});
</script>
